import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Heart, Star, ChevronLeft, ChevronRight, Instagram, Sparkles, TrendingUp, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 19: YUVA — Youth Energy
 * Style: Youthful, energetic, fun with lime green accents
 * Best for: Gen-Z Indian fashion, Crop Kurtas, Indo-Western Fusion, Printed Sarees
 */
const Template19 = () => {
    const navigate = useNavigate();
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const [email, setEmail] = useState('');
    const [hoveredProduct, setHoveredProduct] = useState(null);
    const [hoveredCategory, setHoveredCategory] = useState(null);
    const trendingScrollRef = useRef(null);
    const touchStartX = useRef(0);

    // Trending Items
    const trending = [
        { id: 1, name: 'Neon Crop Kurta', price: '\u20B91,499', tag: 'HOT', image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 2, name: 'Block Print Palazzo Set', price: '\u20B92,299', tag: 'NEW', image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 3, name: 'Tie-Dye Fusion Saree', price: '\u20B93,999', tag: 'TRENDING', image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 4, name: 'Sequin Party Dupatta', price: '\u20B9999', tag: 'SALE', image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 5, name: 'Denim Kurti Jacket', price: '\u20B92,799', tag: 'NEW', image: 'https://images.pexels.com/photos/6712360/pexels-photo-6712360.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 6, name: 'Embroidered Jogger Set', price: '\u20B93,499', tag: 'HOT', image: 'https://images.pexels.com/photos/7275385/pexels-photo-7275385.jpeg?auto=compress&cs=tinysrgb&w=400' },
    ];

    // Categories
    const categories = [
        { name: 'Crop Kurtas', count: '42 styles', image: 'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { name: 'Fusion Wear', count: '38 styles', image: 'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { name: 'Printed Sarees', count: '29 styles', image: 'https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { name: 'Streetwear', count: '35 styles', image: 'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=400' },
    ];

    // Products
    const products = [
        { id: 1, name: 'Lime Crop Kurta Set', price: '\u20B92,499', originalPrice: '\u20B93,499', badge: 'NEW', image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400', rating: 4.8 },
        { id: 2, name: 'Printed Palazzo Suit', price: '\u20B93,299', badge: null, image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400', rating: 4.6 },
        { id: 3, name: 'Indo-Western Fusion Dress', price: '\u20B94,999', originalPrice: '\u20B96,999', badge: 'SALE', image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=400', rating: 4.9 },
        { id: 4, name: 'Statement Dupatta Set', price: '\u20B91,999', badge: null, image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=400', rating: 4.5 },
        { id: 5, name: 'Embroidered Denim Kurta', price: '\u20B93,799', badge: 'HOT', image: 'https://images.pexels.com/photos/6712360/pexels-photo-6712360.jpeg?auto=compress&cs=tinysrgb&w=400', rating: 4.7 },
        { id: 6, name: 'Block Print Saree', price: '\u20B95,499', badge: null, image: 'https://images.pexels.com/photos/7275385/pexels-photo-7275385.jpeg?auto=compress&cs=tinysrgb&w=400', rating: 4.8 },
        { id: 7, name: 'Boho Fusion Jacket', price: '\u20B92,999', originalPrice: '\u20B94,299', badge: 'NEW', image: 'https://images.pexels.com/photos/8953474/pexels-photo-8953474.jpeg?auto=compress&cs=tinysrgb&w=400', rating: 4.6 },
        { id: 8, name: 'Mirror Work Crop Top', price: '\u20B91,799', badge: null, image: 'https://images.pexels.com/photos/2955375/pexels-photo-2955375.jpeg?auto=compress&cs=tinysrgb&w=400', rating: 4.4 },
    ];

    // Social proof images
    const socialImages = [
        'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=400',
        'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=400',
        'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400',
        'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400',
        'https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=400',
        'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=400',
    ];

    // Testimonials
    const testimonials = [
        { text: 'YUVA gets our generation. The crop kurta set I ordered is literally the vibe \u2014 wore it to three festivals already!', name: 'Ananya Sharma', city: 'Mumbai', rating: 5 },
        { text: 'Finally, Indian wear that doesn\'t look like my mom\'s wardrobe. The fusion pieces are chef\'s kiss. Obsessed!', name: 'Riya Patel', city: 'Bangalore', rating: 5 },
        { text: 'The quality for the price is unreal. My printed saree got so many compliments at my friend\'s sangeet. 10/10 recommend.', name: 'Kavya Reddy', city: 'Hyderabad', rating: 5 },
    ];

    // Auto-rotate testimonials
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial((prev) => diff > 0 ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length);
        }
    };

    const toggleWishlist = (e, id) => { e.stopPropagation(); setWishlist((prev) => ({ ...prev, [id]: !prev[id] })); };

    const scrollTrending = (direction) => {
        if (trendingScrollRef.current) {
            const scrollAmount = 300;
            trendingScrollRef.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
        }
    };

    return (
        <div style={{ fontFamily: "'Urbanist', -apple-system, BlinkMacSystemFont, sans-serif" }} className="w-full min-h-screen bg-white">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Urbanist:wght@300;400;500;600;700;800&display=swap');
                .yuva-trending-scroll::-webkit-scrollbar { display: none; }
                .yuva-trending-scroll { -ms-overflow-style: none; scrollbar-width: none; }
            `}</style>

            {/* ===== SECTION 1: BOLD TEXT HERO ===== */}
            <section className="relative min-h-screen w-full overflow-hidden bg-white">
                <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 pt-28 sm:pt-32 lg:pt-36 pb-16 lg:pb-24">
                    <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
                        {/* Left: Bold Text */}
                        <div className="flex-1 text-center lg:text-left">
                            <div className="inline-flex items-center gap-2 bg-[#F7FEE7] text-[#65A30D] px-4 py-2 rounded-full text-xs sm:text-sm font-semibold tracking-wide uppercase mb-6">
                                <Zap className="w-3.5 h-3.5" />
                                New Season Drop
                            </div>
                            <h1
                                style={{ fontFamily: "'Urbanist', sans-serif" }}
                                className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-extrabold text-[#1A1A1A] leading-[0.95] tracking-tight mb-6"
                            >
                                GEN-Z<br />
                                <span className="text-[#65A30D]">INDIAN</span><br />
                                FASHION
                            </h1>
                            <p className="text-[#6B7280] text-base sm:text-lg font-light max-w-md mx-auto lg:mx-0 mb-8 leading-relaxed">
                                Bold. Fresh. Unapologetically Indian. Redefining ethnic wear for the generation that breaks rules.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                                <button
                                    onClick={() => navigate('/template19/shop')}
                                    className="inline-flex items-center justify-center gap-2 bg-[#65A30D] text-white px-8 py-4 rounded-full text-sm font-semibold tracking-wide uppercase hover:bg-[#4D7C0F] transition-all duration-300 hover:shadow-lg hover:shadow-[#65A30D]/25"
                                >
                                    Shop the Drop <ArrowRight className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => navigate('/template19/about')}
                                    className="inline-flex items-center justify-center gap-2 border-2 border-[#65A30D] text-[#65A30D] px-8 py-4 rounded-full text-sm font-semibold tracking-wide uppercase hover:bg-[#65A30D] hover:text-white transition-all duration-300"
                                >
                                    Our Story
                                </button>
                            </div>
                            <div className="flex gap-8 mt-10 justify-center lg:justify-start">
                                <div>
                                    <p className="text-2xl sm:text-3xl font-bold text-[#1A1A1A]">50K+</p>
                                    <p className="text-xs text-[#6B7280] font-medium uppercase tracking-wide">Happy Customers</p>
                                </div>
                                <div className="w-px bg-gray-200" />
                                <div>
                                    <p className="text-2xl sm:text-3xl font-bold text-[#1A1A1A]">500+</p>
                                    <p className="text-xs text-[#6B7280] font-medium uppercase tracking-wide">Unique Styles</p>
                                </div>
                                <div className="w-px bg-gray-200" />
                                <div>
                                    <p className="text-2xl sm:text-3xl font-bold text-[#1A1A1A]">4.9</p>
                                    <p className="text-xs text-[#6B7280] font-medium uppercase tracking-wide">Avg Rating</p>
                                </div>
                            </div>
                        </div>

                        {/* Right: 4-Image Collage Grid */}
                        <div className="flex-1 w-full max-w-md lg:max-w-lg">
                            <div className="grid grid-cols-2 gap-3 sm:gap-4">
                                <div className="rounded-2xl overflow-hidden aspect-[3/4]">
                                    <img src="https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=400" alt="Fashion 1" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                                </div>
                                <div className="rounded-2xl overflow-hidden aspect-[3/4] mt-8">
                                    <img src="https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=400" alt="Fashion 2" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                                </div>
                                <div className="rounded-2xl overflow-hidden aspect-[3/4] -mt-8">
                                    <img src="https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=400" alt="Fashion 3" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                                </div>
                                <div className="rounded-2xl overflow-hidden aspect-[3/4]">
                                    <img src="https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=400" alt="Fashion 4" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 2: TRENDING NOW ===== */}
            <section className="py-16 sm:py-20 lg:py-24 bg-[#F7FEE7]">
                <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
                    <div className="flex items-center justify-between mb-8 sm:mb-10">
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <TrendingUp className="w-5 h-5 text-[#65A30D]" />
                                <span className="text-[#65A30D] text-xs font-bold uppercase tracking-widest">What's Hot</span>
                            </div>
                            <h2 style={{ fontFamily: "'Urbanist', sans-serif" }} className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1A1A]">Trending Now</h2>
                        </div>
                        <div className="hidden sm:flex gap-2">
                            <button onClick={() => scrollTrending('left')} className="w-10 h-10 rounded-full border-2 border-[#65A30D] text-[#65A30D] flex items-center justify-center hover:bg-[#65A30D] hover:text-white transition-all">
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <button onClick={() => scrollTrending('right')} className="w-10 h-10 rounded-full bg-[#65A30D] text-white flex items-center justify-center hover:bg-[#4D7C0F] transition-all">
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                    <div ref={trendingScrollRef} className="yuva-trending-scroll flex gap-5 overflow-x-auto pb-4">
                        {trending.map((item) => (
                            <div
                                key={item.id}
                                onClick={() => navigate(`/template19/product/${item.id}`)}
                                className="flex-shrink-0 w-[260px] sm:w-[280px] bg-white rounded-2xl overflow-hidden cursor-pointer group shadow-sm hover:shadow-xl transition-all duration-300"
                            >
                                <div className="relative aspect-[3/4] overflow-hidden">
                                    <img src={item.image} alt={item.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                    <span className="absolute top-3 left-3 bg-[#65A30D] text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                                        {item.tag}
                                    </span>
                                </div>
                                <div className="p-4">
                                    <h3 className="text-sm font-semibold text-[#1A1A1A] mb-1">{item.name}</h3>
                                    <p className="text-[#65A30D] font-bold text-base">{item.price}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 3: CATEGORIES ===== */}
            <section className="py-16 sm:py-20 lg:py-24 bg-white">
                <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
                    <div className="text-center mb-10 sm:mb-14">
                        <span className="text-[#65A30D] text-xs font-bold uppercase tracking-widest mb-2 block">Explore</span>
                        <h2 style={{ fontFamily: "'Urbanist', sans-serif" }} className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1A1A]">Shop by Vibe</h2>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                        {categories.map((cat, index) => (
                            <div
                                key={cat.name}
                                onClick={() => navigate('/template19/shop')}
                                onMouseEnter={() => setHoveredCategory(index)}
                                onMouseLeave={() => setHoveredCategory(null)}
                                className="relative rounded-2xl overflow-hidden cursor-pointer group aspect-[3/4]"
                            >
                                <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
                                    <h3 className="text-white text-lg sm:text-xl font-bold mb-1">{cat.name}</h3>
                                    <p className="text-white/70 text-xs font-medium">{cat.count}</p>
                                    <div
                                        className="mt-3 inline-flex items-center gap-1 text-[#65A30D] text-xs font-semibold uppercase tracking-wide transition-all duration-300"
                                        style={{ opacity: hoveredCategory === index ? 1 : 0, transform: hoveredCategory === index ? 'translateY(0)' : 'translateY(8px)' }}
                                    >
                                        Explore <ArrowRight className="w-3 h-3" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 4: PRODUCTS GRID ===== */}
            <section className="py-16 sm:py-20 lg:py-24 bg-[#FAFAFA]">
                <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-10 sm:mb-14 gap-4">
                        <div>
                            <span className="text-[#65A30D] text-xs font-bold uppercase tracking-widest mb-2 block">Curated</span>
                            <h2 style={{ fontFamily: "'Urbanist', sans-serif" }} className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1A1A]">Fresh Picks</h2>
                        </div>
                        <button
                            onClick={() => navigate('/template19/shop')}
                            className="inline-flex items-center gap-2 text-[#65A30D] text-sm font-semibold hover:gap-3 transition-all duration-300"
                        >
                            View All <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate(`/template19/product/${product.id}`)}
                                onMouseEnter={() => setHoveredProduct(product.id)}
                                onMouseLeave={() => setHoveredProduct(null)}
                                className="bg-white rounded-2xl overflow-hidden cursor-pointer group shadow-sm hover:shadow-xl transition-all duration-300"
                            >
                                <div className="relative aspect-[3/4] overflow-hidden">
                                    <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                    {product.badge && (
                                        <span className="absolute top-3 left-3 bg-[#65A30D] text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                                            {product.badge}
                                        </span>
                                    )}
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center transition-all hover:scale-110"
                                    >
                                        <Heart className={`w-4 h-4 ${wishlist[product.id] ? 'fill-red-500 text-red-500' : 'text-[#1A1A1A]'}`} />
                                    </button>
                                    <div
                                        className="absolute bottom-0 left-0 right-0 p-3 transition-all duration-300"
                                        style={{ opacity: hoveredProduct === product.id ? 1 : 0, transform: hoveredProduct === product.id ? 'translateY(0)' : 'translateY(100%)' }}
                                    >
                                        <button className="w-full bg-[#65A30D] text-white text-xs font-semibold py-2.5 rounded-full uppercase tracking-wide hover:bg-[#4D7C0F] transition-colors">
                                            Quick Add
                                        </button>
                                    </div>
                                </div>
                                <div className="p-4">
                                    <div className="flex items-center gap-1 mb-1.5">
                                        {[...Array(5)].map((_, i) => (
                                            <Star key={i} className={`w-3 h-3 ${i < Math.floor(product.rating) ? 'fill-[#65A30D] text-[#65A30D]' : 'text-gray-200'}`} />
                                        ))}
                                        <span className="text-[10px] text-[#6B7280] ml-1">{product.rating}</span>
                                    </div>
                                    <h3 className="text-sm font-semibold text-[#1A1A1A] mb-1 line-clamp-1">{product.name}</h3>
                                    <div className="flex items-center gap-2">
                                        <p className="text-[#65A30D] font-bold text-base">{product.price}</p>
                                        {product.originalPrice && (
                                            <p className="text-[#6B7280] text-xs line-through">{product.originalPrice}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 5: SOCIAL PROOF ===== */}
            <section className="py-16 sm:py-20 lg:py-24 bg-white">
                <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
                    <div className="text-center mb-10 sm:mb-14">
                        <div className="inline-flex items-center gap-2 mb-3">
                            <Instagram className="w-5 h-5 text-[#65A30D]" />
                            <span className="text-[#65A30D] text-xs font-bold uppercase tracking-widest">#YuvaStyle</span>
                        </div>
                        <h2 style={{ fontFamily: "'Urbanist', sans-serif" }} className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1A1A]">As Seen On You</h2>
                        <p className="text-[#6B7280] text-sm sm:text-base mt-3 max-w-lg mx-auto">Tag us in your YUVA fits and get featured. Your style, our tribe.</p>
                    </div>
                    <div className="grid grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
                        {socialImages.map((img, index) => (
                            <div key={index} className="aspect-square rounded-xl overflow-hidden cursor-pointer group relative">
                                <img src={img} alt={`Social ${index + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                <div className="absolute inset-0 bg-[#65A30D]/0 group-hover:bg-[#65A30D]/30 transition-colors duration-300 flex items-center justify-center">
                                    <Instagram className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 6: TESTIMONIALS ===== */}
            <section className="py-16 sm:py-20 lg:py-24 bg-[#FAFAFA]">
                <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
                    <div className="text-center mb-10 sm:mb-14">
                        <span className="text-[#65A30D] text-xs font-bold uppercase tracking-widest mb-2 block">Reviews</span>
                        <h2 style={{ fontFamily: "'Urbanist', sans-serif" }} className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1A1A]">The Tribe Speaks</h2>
                    </div>
                    <div
                        className="max-w-2xl mx-auto text-center"
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    >
                        <div className="flex justify-center gap-1 mb-6">
                            {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                                <Star key={i} className="w-5 h-5 fill-[#65A30D] text-[#65A30D]" />
                            ))}
                        </div>
                        <p className="text-lg sm:text-xl lg:text-2xl text-[#1A1A1A] font-light leading-relaxed mb-8">
                            "{testimonials[activeTestimonial].text}"
                        </p>
                        <div>
                            <p className="text-[#1A1A1A] font-semibold text-base">{testimonials[activeTestimonial].name}</p>
                            <p className="text-[#6B7280] text-sm">{testimonials[activeTestimonial].city}</p>
                        </div>
                        <div className="flex justify-center gap-2 mt-8">
                            {testimonials.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setActiveTestimonial(index)}
                                    className="h-2.5 rounded-full transition-all duration-300"
                                    style={{
                                        backgroundColor: index === activeTestimonial ? '#65A30D' : '#D1D5DB',
                                        width: index === activeTestimonial ? '24px' : '10px',
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 7: NEWSLETTER ===== */}
            <section className="py-16 sm:py-20 lg:py-24 bg-[#65A30D]">
                <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 text-center">
                    <Sparkles className="w-8 h-8 text-white/80 mx-auto mb-4" />
                    <h2 style={{ fontFamily: "'Urbanist', sans-serif" }} className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">Join the Tribe</h2>
                    <p className="text-white/80 text-sm sm:text-base max-w-md mx-auto mb-8">
                        Get first dibs on new drops, exclusive deals, and styling tips delivered to your inbox.
                    </p>
                    <form
                        onSubmit={(e) => { e.preventDefault(); if (email.trim()) setEmail(''); }}
                        className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
                    >
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email"
                            className="flex-1 px-5 py-3.5 rounded-full text-sm bg-white/20 text-white placeholder-white/60 border border-white/30 outline-none focus:border-white transition-colors"
                        />
                        <button
                            type="submit"
                            className="px-8 py-3.5 rounded-full bg-white text-[#65A30D] text-sm font-bold uppercase tracking-wide hover:bg-[#F7FEE7] transition-colors"
                        >
                            Subscribe
                        </button>
                    </form>
                </div>
            </section>
        </div>
    );
};

export default Template19;
